import { supabaseClient } from "@/supabase/app";
import {
  CompanyFilters,
  CompanyProfileFilters,
  ContactFilters,
  ContactProfileFilter,
  DepartmentHierarchy,
  FiltersState,
  JobTitleSearch,
  KeywordsAndTopics,
  SearchOptions,
  WebsiteContentFilters,
} from "@/types/filters";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack";

const initialState: FiltersState = {
  companyFilters: {
    companySearch: {
      searchKeywords: [],
      searchOptions: {
        companyName: false,
        companyDescription: false,
        domainName: false,
      },
    },
    websiteContent: {
      searchKeywords: [],
      searchOptions: {
        option1: "",
        option2: "",
        option3: "",
      },
    },
    keywordsAndTopics: {
      searchKeywords: [],
      keywordMatch: "any",
      suggestions: {
        Expertise: false,
        Accessories: false,
        Finance: false,
        Training: false,
        Mobile: false,
      },
    },
    companySize: { from: 0, to: 0,unknownSize:false },
    companyProfile: {
      requirements: {
        websiteAvailable: false,
        phoneNumberAvailable: false,
        emailAddressAvailable: false,
        vatNumberAvailable: false,
        registerNumberAvailable: false,
      },
      socialMedia: {
        linkedIn: false,
        xing: false,
        facebook: false,
        twitter: false,
        youtube: false,
        instagram: false,
        pinterest: false,
      },
      dataCompleteness: 0,
    },
  },
  contactFilters: {
    departmentHierarchy: {
      searchOptions: {},
      hierarchy: {},
    },
    jobTitleSearch: {
      searchKeywords: [],
      suggestions: {
        contentManagement: false,
        contentManagement1: false,
        contentManagement2: false,
      },
    },
    contactLocation: {},
    contactProfile: {
      phoneNumber: {
        mobileNumber: false,
        directDialNumber: false,
        companyPhoneNumber: false,
      },
      emailAddress: {
        emailAvailable: false,
        emailSourceLink: false,
        emailValid: false,
      },
      contactSources: {
        linkedinProfile: false,
        xingProfile: false,
        businessRegister: false,
        companyWebsite: false,
        otherWebSources: false,
      },
    },
  },
  softwareAndSystems: {
    softwareSystems: [],
    includeUnknownShopSystem: false,
  },
  industries: {
    selectedIndustries: [],
    industry: {},
    wzCodes: [],
    naceCodes: [],
  },
  regionalFilters: { countryFilter: {}, radius: { location: "", radius: 0 } },
  newsAndTriggerEvents: {
    events: [],
    publicationPeriod: "",
    eventDate: "",
    searchCriteria: "",
  },
  financialFigures: { from: 0, to: 0,unknownSize:false,currency:"" },
};

const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    saveFilter(state, action: PayloadAction<FiltersState>) {
      return action.payload;
    },
    updateCompanyFilters: (
      state,
      action: PayloadAction<Partial<CompanyFilters>>
    ) => {
      state.companyFilters = { ...state.companyFilters, ...action.payload };
    },
    addCompanySearchKeywords: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.companyFilters.companySearch.searchKeywords)) {
        state.companyFilters.companySearch.searchKeywords = [];
      }
      if (
        !state.companyFilters.companySearch.searchKeywords.includes(
          action.payload
        )
      ) {
        state.companyFilters.companySearch.searchKeywords.push(action.payload);
      }
    },
    removeCompanySearchKeywords: (state, action: PayloadAction<string>) => {
      if (Array.isArray(state.companyFilters.companySearch.searchKeywords)) {
        state.companyFilters.companySearch.searchKeywords =
          state.companyFilters.companySearch.searchKeywords.filter(
            (keyword) => keyword !== action.payload
          );
      }
    },
    updateCompanySearchOptions: (
      state,
      action: PayloadAction<Partial<SearchOptions>>
    ) => {
      console.log("fgh", action.payload);
      state.companyFilters.companySearch.searchOptions = {
        ...state.companyFilters.companySearch.searchOptions,
        ...action.payload,
      };
    },
    updateWebsiteContentFilters: (
      state,
      action: PayloadAction<Partial<WebsiteContentFilters>>
    ) => {
      state.companyFilters.websiteContent.searchOptions = {
        ...state.companyFilters.websiteContent.searchOptions,
        ...action.payload,
      };
    },
    addWebsiteContentSearchKeywords: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.companyFilters.websiteContent.searchKeywords)) {
        state.companyFilters.websiteContent.searchKeywords = [];
      }
      if (
        !state.companyFilters.websiteContent.searchKeywords.includes(
          action.payload
        )
      ) {
        state.companyFilters.websiteContent.searchKeywords.push(action.payload);
      }
    },
    removeWebsiteContentSearchKeywords: (
      state,
      action: PayloadAction<string>
    ) => {
      if (Array.isArray(state.companyFilters.websiteContent.searchKeywords)) {
        state.companyFilters.websiteContent.searchKeywords =
          state.companyFilters.websiteContent.searchKeywords.filter(
            (keyword) => keyword !== action.payload
          );
      }
    },
    updateCompanyProfile: (
      state,
      action: PayloadAction<Partial<CompanyProfileFilters>>
    ) => {
      state.companyFilters.companyProfile = {
        ...state.companyFilters.companyProfile,
        ...action.payload,
      };
    },
    updateDataCompleteness: (state, action: PayloadAction<number>) => {
      state.companyFilters.companyProfile.dataCompleteness = action.payload;
    },
    updateKeywordsAndTopics: (
      state,
      action: PayloadAction<Partial<KeywordsAndTopics>>
    ) => {
      state.companyFilters.keywordsAndTopics = {
        ...state.companyFilters.keywordsAndTopics,
        ...action.payload,
      };
    },
    addKeywordsAndTopicsSearchKeywords: (
      state,
      action: PayloadAction<string>
    ) => {
      if (
        !Array.isArray(state.companyFilters.keywordsAndTopics.searchKeywords)
      ) {
        state.companyFilters.keywordsAndTopics.searchKeywords = [];
      }
      if (
        !state.companyFilters.keywordsAndTopics.searchKeywords.includes(
          action.payload
        )
      ) {
        state.companyFilters.keywordsAndTopics.searchKeywords.push(
          action.payload
        );
      }
    },
    removeKeywordsAndTopicsSearchKeywords: (
      state,
      action: PayloadAction<string>
    ) => {
      if (
        Array.isArray(state.companyFilters.keywordsAndTopics.searchKeywords)
      ) {
        state.companyFilters.keywordsAndTopics.searchKeywords =
          state.companyFilters.keywordsAndTopics.searchKeywords.filter(
            (keyword) => keyword !== action.payload
          );
      }
    },
    updateJobTitleSearch: (
      state,
      action: PayloadAction<Partial<JobTitleSearch>>
    ) => {
      state.contactFilters.jobTitleSearch = {
        ...state.contactFilters.jobTitleSearch,
        ...action.payload,
      };
    },
    addJobTitleSearchKeywords: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.contactFilters.jobTitleSearch.searchKeywords)) {
        state.contactFilters.jobTitleSearch.searchKeywords = [];
      }
      if (
        !state.contactFilters.jobTitleSearch.searchKeywords.includes(
          action.payload
        )
      ) {
        state.contactFilters.jobTitleSearch.searchKeywords.push(action.payload);
      }
    },
    removeJobTitleSearchKeywords: (state, action: PayloadAction<string>) => {
      if (Array.isArray(state.contactFilters.jobTitleSearch.searchKeywords)) {
        state.contactFilters.jobTitleSearch.searchKeywords =
          state.contactFilters.jobTitleSearch.searchKeywords.filter(
            (keyword) => keyword !== action.payload
          );
      }
    },
    updateCompanySize: (
      state,
      action: PayloadAction<{ from: number; to: number; unknownSize: boolean }>
    ) => {
      state.companyFilters.companySize = action.payload;
    },
    updateContactFilters: (
      state,
      action: PayloadAction<Partial<ContactFilters>>
    ) => {
      state.contactFilters = { ...state.contactFilters, ...action.payload };
    },
    updateContactProfile: (
      state,
      action: PayloadAction<Partial<ContactProfileFilter>>
    ) => {
      const updates = action.payload;
      state.contactFilters.contactProfile = {
        ...state.contactFilters.contactProfile,
        ...updates,
      };
    },
    updateDepartmentHierarchy: (
      state,
      action: PayloadAction<Partial<DepartmentHierarchy>>
    ) => {
      const updates = action.payload;
      state.contactFilters.departmentHierarchy = {
        ...state.contactFilters.departmentHierarchy,
        ...updates,
      };
    },
    addIndustry: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.industries?.selectedIndustries)) {
        state.industries.selectedIndustries = [];
      }
      if (!state.industries.selectedIndustries.includes(action.payload)) {
        state.industries.selectedIndustries.push(action.payload);
      }
    },
    removeIndustry: (state, action: PayloadAction<string>) => {
      if (Array.isArray(state.industries.selectedIndustries)) {
        state.industries.selectedIndustries =
          state.industries.selectedIndustries.filter(
            (industry) => industry !== action.payload
          );
      }
    },
    addWZCodes: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.industries?.wzCodes)) {
        state.industries.wzCodes = [];
      }
      if (!state.industries.wzCodes.includes(action.payload)) {
        state.industries.wzCodes.push(action.payload);
      }
    },
    removeWZCodes: (state, action: PayloadAction<string>) => {
      if (Array.isArray(state.industries.wzCodes)) {
        state.industries.wzCodes = state.industries.wzCodes.filter(
          (industry) => industry !== action.payload
        );
      }
    },
    addNACECodes: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.industries?.naceCodes)) {
        state.industries.naceCodes = [];
      }
      if (!state.industries.naceCodes.includes(action.payload)) {
        state.industries.naceCodes.push(action.payload);
      }
    },
    removeNACECodes: (state, action: PayloadAction<string>) => {
      if (Array.isArray(state.industries.naceCodes)) {
        state.industries.naceCodes = state.industries.naceCodes.filter(
          (industry) => industry !== action.payload
        );
      }
    },
    updateSearchOptions: (
      state,
      action: PayloadAction<Record<string, boolean>>
    ) => {
      state.contactFilters.departmentHierarchy.searchOptions = action.payload;
    },
    updateHierarchy: (
      state,
      action: PayloadAction<Record<string, boolean>>
    ) => {
      state.contactFilters.departmentHierarchy.hierarchy = action.payload;
    },
    updateContactLocation: (
      state,
      action: PayloadAction<Record<string, boolean>>
    ) => {
      console.log("Updating contactLocation with:", action.payload);
      state.contactFilters.contactLocation = action.payload;
    },
    updateSoftwareSystems(state, action: PayloadAction<string[]>) {
      state.softwareAndSystems.softwareSystems = action.payload;
    },
    toggleIncludeUnknownShopSystem(state) {
      state.softwareAndSystems.includeUnknownShopSystem =
        !state?.softwareAndSystems.includeUnknownShopSystem;
    },
    updateConuntryLocation: (
      state,
      action: PayloadAction<Record<string, boolean>>
    ) => {
      console.log("Updating contactLocation with:", action.payload);
      state.regionalFilters.countryFilter = action.payload;
    },
    updateLocation: (
      state,
      action: PayloadAction<{ location: string; radius: number }>
    ) => {
      state.regionalFilters.radius = action.payload;
    },
    updateTriggerEvents(state, action: PayloadAction<string[]>) {
      state.newsAndTriggerEvents.events = action.payload;
    },
    updatePublicationPeriod(state, action: PayloadAction<string>) {
      state.newsAndTriggerEvents.publicationPeriod = action.payload;
    },
    updateEventDate(state, action: PayloadAction<string>) {
      state.newsAndTriggerEvents.eventDate = action.payload;
    },
    updateSearchCriteria(state, action: PayloadAction<string>) {
      state.newsAndTriggerEvents.searchCriteria = action.payload;
    },
    updateFinancialFigures: (
      state,
      action: PayloadAction<{ from: number; to: number ,unknownSize:boolean,currency:string}>
    ) => {
      state.financialFigures = action.payload;
    },
    clearFilters: (state) => {
      return initialState;
    },
  },
});

export const {
  updateCompanyFilters,
  updateCompanySearchOptions,
  addCompanySearchKeywords,
  removeCompanySearchKeywords,
  updateWebsiteContentFilters,
  updateKeywordsAndTopics,
  updateCompanySize,
  updateDepartmentHierarchy,
  updateHierarchy,
  saveFilter,
  updateSearchOptions,
  updateCompanyProfile,
  addIndustry,
  removeIndustry,
  addKeywordsAndTopicsSearchKeywords,
  removeKeywordsAndTopicsSearchKeywords,
  addWebsiteContentSearchKeywords,
  removeWebsiteContentSearchKeywords,
  addJobTitleSearchKeywords,
  removeJobTitleSearchKeywords,
  addNACECodes,
  addWZCodes,
  removeNACECodes,
  removeWZCodes,
  updateJobTitleSearch,
  updateLocation,
  updateContactLocation,
  updateContactProfile,
  updateDataCompleteness,
  updateContactFilters,
  updateSoftwareSystems,
  toggleIncludeUnknownShopSystem,
  updateConuntryLocation,
  updateTriggerEvents,
  updatePublicationPeriod,
  updateEventDate,
  updateSearchCriteria,
  updateFinancialFigures,
  clearFilters,
} = filtersSlice.actions;

export default filtersSlice.reducer;

export async function saveFiltersState(
  id: number,
  filterData: FiltersState,
  name:string,
  handleClearData: () => void
) {
  try {
    console.log(name);
    const filteredData = JSON.stringify(filterData);
    const { data, error } = await supabaseClient.from("targetMarkets").insert([
      {
        user_id: id,
        filters: filteredData,
        name
      },
    ]);

    if (error) {
      throw error;
    }

    enqueueSnackbar("Filters state saved successfully!", {
      variant: "success",
    });
    handleClearData();
    return data;
  } catch (error) {
    enqueueSnackbar(`Error saving filters state: ${error.message}`, {
      variant: "error",
    });
  }
}
interface TargetMarket {
  created_at: string | number | Date;
  id: number;
  user_id: string;
  filters: string;
  name:string;
  status: string;
}

export async function getAllTargetMarkets(): Promise<TargetMarket[] | null> {
  try {
    const { data, error } = await supabaseClient
      .from("targetMarkets")
      .select("*");

    if (error) {
      throw error;
    }
    return data as TargetMarket[];
  } catch (error) {
    enqueueSnackbar(`Error fetching target markets: ${error.message}`, {
      variant: "error",
    });
    return null;
  }
}

export async function getTargetMarketsById(
  userId: string
): Promise<TargetMarket[] | null> {
  try {
    console.log("first",userId)
    const { data, error } = await supabaseClient
      .from("targetMarkets")
      .select("*")
      .eq("user_id", userId);

    if (error) {
      throw error;
    }

    return data as TargetMarket[];
  } catch (error) {
    enqueueSnackbar(`Error fetching target markets: ${error.message}`, {
      variant: "error",
    });
    return null;
  }
}

export async function updateTargetMarketStatus(
  id: number,
  newStatus: string
): Promise<void> {
  try {
    const { data, error } = await supabaseClient
      .from("targetMarkets")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      throw error;
    }

    enqueueSnackbar("Status updated successfully!", {
      variant: "success",
    });
    // return data;
  } catch (error) {
    enqueueSnackbar(`Error updating status: ${error.message}`, {
      variant: "error",
    });
  }
}

export async function updateTargetMarketFilters(
  id: number,
  filterData: FiltersState,
  name:string
): Promise<void> {
  try {
    const filteredData = JSON.stringify(filterData);
    const { data, error } = await supabaseClient
      .from("targetMarkets")
      .update({ filters: filteredData,name })
      .eq("id", id);

    if (error) {
      throw error;
    }

    enqueueSnackbar("Filters updated successfully!", {
      variant: "success",
    });
    // return data;
  } catch (error) {
    enqueueSnackbar(`Error updating filters: ${error.message}`, {
      variant: "error",
    });
  }
}
export async function deleteTargetMarket(id: number): Promise<void> {
  try {
    const { data, error } = await supabaseClient
      .from("targetMarkets")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }

    enqueueSnackbar("Target market deleted successfully!", {
      variant: "success",
    });
  } catch (error) {
    enqueueSnackbar(`Error deleting target market: ${error.message}`, {
      variant: "error",
    });
  }
}
